import { SkipNext } from '@mui/icons-material';
import {
    Box,
    Button,
    Stack,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { Check, X } from 'lucide-react';
import { flushSync } from 'react-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import useRoi from '~/hooks/structure-set/useRoi';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';

export default function ReviewControls({ roi }: { roi: RoiReadModel }) {
    const { setRoiApprovalStatus } = useRoi(roi);
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const setSelectedROI = useSetRecoilState<number>(selectedROIState);
    const { palette } = useTheme();

    const handleReview = (status?: DICOMApprovalStatus) => {
        if (!currentStructureSet?.roiArray) {
            return;
        }

        const roiArray = [...currentStructureSet.roiArray].sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { numeric: true })
        );
        const currentIndex = roiArray.findIndex(
            (r) => r.roiNumber === roi.roiNumber
        );

        if (currentIndex !== -1) {
            const nextIndex = (currentIndex + 1) % roiArray.length;
            const nextRoi = roiArray[nextIndex];
            // Flush the selection change synchronously so React renders the
            // new ROI panel before the (potentially expensive) status domain
            // event fires.
            flushSync(() => setSelectedROI(nextRoi.roiNumber));
        }

        if (status) {
            setRoiApprovalStatus(status);
        }
    };

    const reviewedStructuresAmount = currentStructureSet?.roiArray.filter(
        (struct) => struct.approvalStatus !== DICOMApprovalStatus.UNAPPROVED
    ).length;

    return (
        <Stack spacing={0.5}>
            <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" align="center" fontSize={12}>
                    Structures reviewed
                </Typography>
                <Typography variant="body2" align="center" fontSize={12}>
                    {reviewedStructuresAmount} /{' '}
                    {currentStructureSet?.roiArray.length}{' '}
                </Typography>
            </Box>
            <Stack spacing={1} direction="row" justifyContent="space-evenly">
                <Tooltip title="Approve">
                    <Button
                        variant="outlined"
                        color="success"
                        fullWidth
                        size="small"
                        onClick={() =>
                            handleReview(DICOMApprovalStatus.APPROVED)
                        }
                        sx={{
                            ...(roi.approvalStatus ===
                            DICOMApprovalStatus.APPROVED
                                ? {
                                      backgroundColor: 'success.main',
                                      color: 'success.contrastText',
                                  }
                                : {}),
                        }}
                        data-testid="approve-button"
                    >
                        <Check
                            color={
                                roi.approvalStatus ===
                                DICOMApprovalStatus.APPROVED
                                    ? palette.success.contrastText
                                    : palette.success.main
                            }
                        />
                    </Button>
                </Tooltip>
                <Tooltip title="Reject">
                    <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        size="small"
                        onClick={() =>
                            handleReview(DICOMApprovalStatus.REJECTED)
                        }
                        sx={{
                            ...(roi.approvalStatus ===
                            DICOMApprovalStatus.REJECTED
                                ? {
                                      backgroundColor: 'error.main',
                                      color: 'error.contrastText',
                                  }
                                : {}),
                        }}
                    >
                        <X
                            color={
                                roi.approvalStatus ===
                                DICOMApprovalStatus.REJECTED
                                    ? palette.error.contrastText
                                    : palette.error.main
                            }
                        />
                    </Button>
                </Tooltip>
                <Tooltip title="Next">
                    <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={() => handleReview()}
                        sx={{
                            fontSize: 12,
                        }}
                    >
                        <SkipNext />
                    </Button>
                </Tooltip>
            </Stack>
        </Stack>
    );
}
